import { useEnumHelpers } from '@/queries/useEnumHelpers';
import { AreaEnum } from '@/queries/useEnums.ts';
import { Grow } from '@mui/material';
import React, { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { IconWrapper } from '../../components/icon/IconWrapper.tsx';
import { BuildingParameters } from '../../stores/SubprojectStore.ts';
import { SpaceUsageTile } from './SpaceUsageTile.tsx';

interface SpaceUtilizationProps {
  keyId: string;
  buildingParams: BuildingParameters;
  save: () => void;
  form: UseFormReturn;
  readOnly?: boolean;
}

/**
 * Space Utilization for each building parameter set
 */
export const SpaceUtilization: React.FC<SpaceUtilizationProps> = ({
  keyId,
  buildingParams,
  save,
  readOnly,
  form: { register, formState, setValue },
}) => {
  // Extract areaUnits from the enums hook, providing a default empty array.
  const { areaUnits = [] } = useEnumHelpers();
  // Assert areaUnits as an array of AreaUnit objects.
  // const areaUnitsArray: AreaEnum[] = Array.isArray(areaUnits)
  //   ? (areaUnits as AreaEnum[])
  //   : typeof areaUnits === 'object'
  //     ? Object.values(areaUnits)
  //     : [];

  // Fallback area units if API data is not available
  const fallbackAreaUnits: AreaEnum[] = [
    { name: 'SquareMeters', symbol: 'm²', unitPerM2: 1 },
    { name: 'sqft', symbol: 'ft²', unitPerM2: 10.76391042 },
    { name: 'Hectares', symbol: 'ha', unitPerM2: 0.0001 },
    { name: 'Acre', symbol: 'acres', unitPerM2: 0.0002471053815 },
  ];
  const finalAreaUnits = areaUnits.length > 0 ? areaUnits : fallbackAreaUnits;

  // Ensure the buildingParams sub-objects are defined
  if (!buildingParams.conditionedSpace) {
    buildingParams.conditionedSpace = { value: 0, unit: 'SquareMeters' };
  }
  if (!buildingParams.perviousArea) {
    buildingParams.perviousArea = { value: 0, unit: 'SquareMeters' };
  }

  // Set default unit if empty
  if (!buildingParams.conditionedSpace.unit) {
    buildingParams.conditionedSpace.unit = 'SquareMeters';
  }
  if (!buildingParams.perviousArea.unit) {
    buildingParams.perviousArea.unit = 'SquareMeters';
  }
  // Initialize form values when keyId changes
  useEffect(() => {
    // Force conditionedSpace and perviousArea to have 2 decimal places
    buildingParams.conditionedSpace.value = parseFloat(
      buildingParams.conditionedSpace.value.toFixed(2),
    );
    buildingParams.perviousArea.value = parseFloat(
      buildingParams.perviousArea.value.toFixed(2),
    );

    // Set form values to match current buildingParams
    setValue(
      `${keyId}-Conditioned Space`,
      buildingParams.conditionedSpace.value,
    );
    setValue(`${keyId}-Landscaped Area`, buildingParams.perviousArea.value);
    setValue(`${keyId}-Number of Floors`, buildingParams.numberOfFloors ?? 0);
    setValue(
      `${keyId}-Average Number of Occupants`,
      buildingParams.numberOfOccupants ?? 0,
    );
  }, [
    keyId,
    setValue,
    buildingParams.conditionedSpace,
    buildingParams.numberOfFloors,
    buildingParams.numberOfOccupants,
    buildingParams.perviousArea,
  ]);

  // Helper function to safely parse values
  const safeParseAndSave = (value: string): number => {
    const parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : 0;
  };

  // Individual onChange handlers for each field
  const handleConditionedSpaceChange = (value: string) => {
    buildingParams.conditionedSpace.value = safeParseAndSave(value);
    if (value === '0') {
      setValue(
        `${keyId}-Conditioned Space`,
        buildingParams.conditionedSpace.value,
      );
    }
    save();
  };

  const handleFloorsChange = (value: string) => {
    buildingParams.numberOfFloors = safeParseAndSave(value);
    if (value === '0') {
      setValue(`${keyId}-Number of Floors`, buildingParams.numberOfFloors ?? 0);
    }
    save();
  };

  return (
    <React.Fragment>
      <Grow timeout={300} in>
        <SpaceUsageTile
          keyId={keyId}
          usage={'Conditioned Space'}
          icon={<IconWrapper icon={'HVAC'} sx={{ height: 96, width: 96 }} />}
          register={register}
          value={buildingParams.conditionedSpace.value}
          unit={(() => {
            const foundUnit = finalAreaUnits.find(
              (v) => v.name === buildingParams.conditionedSpace.unit,
            );
            return foundUnit?.symbol || foundUnit?.name || '';
          })()}
          setUnit={(unit) => {
            buildingParams.conditionedSpace.unit = unit;
            save(); // Trigger re-render after unit change
          }}
          errors={formState.errors}
          onChange={handleConditionedSpaceChange}
          disabled={readOnly}
        />
      </Grow>
      {/* Landscaped Area — suppressed for Alpha Stage 1 (P1, Stage 2 scope) */}
      <Grow timeout={700} in>
        <SpaceUsageTile
          keyId={keyId}
          usage={'Number of Floors'}
          icon={<IconWrapper icon={'Floors'} sx={{ height: 96, width: 96 }} />}
          register={register}
          value={buildingParams.numberOfFloors}
          errors={formState.errors}
          hideUnit
          onChange={handleFloorsChange}
          disabled={readOnly}
        />
      </Grow>
      {/* Average Number of Occupants — suppressed for Alpha Stage 1 (P1, currently unused) */}
    </React.Fragment>
  );
};
